import React from 'react';
import { TextField, Typography, Box, Grid, Button, Paper, Stack, Chip } from '@mui/material';

export const VIEWER_COLORS = {
  primary: '#0d6aee',
  primaryDark: '#1d4ed8',
  surfaceBorder: '#dbe7f3',
  surfaceSoft: '#e2e8f0',
  textMain: '#0f172a',
  textMuted: '#475569',
  labelMuted: '#64748b',
};

export const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 4,
  border: `1px solid ${VIEWER_COLORS.surfaceBorder}`,
  background: '#ffffff',
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#d0d7de' },
    '&:hover fieldset': { borderColor: VIEWER_COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: VIEWER_COLORS.primary, borderWidth: 2 },
  },
};

const ReadOnlyField = ({ label, value }) => (
  <TextField label={label} fullWidth value={value} InputProps={{ readOnly: true }} size="small" sx={fieldSx} />
);

export const CuestionarioSPHero = ({ createdAt }) => (
  <Paper
    className="entrevista-viewer__hero"
    elevation={0}
    sx={{
      p: { xs: 2, md: 3 },
      mb: 1,
      borderRadius: 4,
      border: `1px solid ${VIEWER_COLORS.surfaceBorder}`,
      background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
      boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
    }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={1}
    >
      <Box>
        <Typography variant="overline" sx={{ color: VIEWER_COLORS.primaryDark, letterSpacing: 1.4 }}>
          Sistema de seguimiento clínico
        </Typography>
        <Typography variant="h4" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 600, lineHeight: 1.1, mb: 1 }}>
          Cuestionario Suelo Pélvico
        </Typography>
        <Typography sx={{ color: VIEWER_COLORS.textMuted, maxWidth: 760 }}>
          Valoración de incontinencia urinaria e impacto en la calidad de vida.
        </Typography>
      </Box>
      <Chip
        label={`Registro ${createdAt || '--/--/----'}`}
        sx={{ borderColor: '#bfdbfe', color: '#1e3a8a', fontWeight: 600 }}
        variant="outlined"
      />
    </Stack>
  </Paper>
);

export const PatientProfileSection = ({ patientInfo }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 2 }}>
      Perfil del paciente
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <ReadOnlyField label="Nombre" value={patientInfo.nombre} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ReadOnlyField label="Apellidos" value={patientInfo.apellidos} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ReadOnlyField label="Fecha" value={patientInfo.createdAt} />
      </Grid>
    </Grid>
  </Paper>
);

const getInterpretacion = (score) => {
  if (score === 0)  return { label: 'Sin incontinencia', color: '#166534', bg: '#dcfce7' };
  if (score <= 5)   return { label: 'Leve',              color: '#854d0e', bg: '#fef9c3' };
  if (score <= 12)  return { label: 'Moderada',          color: '#92400e', bg: '#fef3c7' };
  if (score <= 18)  return { label: 'Grave',             color: '#9a3412', bg: '#ffedd5' };
  return                   { label: 'Muy grave',         color: '#991b1b', bg: '#fee2e2' };
};

export const IncontinenciaSection = ({ fields, puntuacionTotal, pFrecuencia, pCantidad }) => {
  const interpretacion = getInterpretacion(puntuacionTotal ?? 0);
  const hasScore = puntuacionTotal != null;

  return (
    <Paper elevation={0} sx={sectionPaperSx}>
      <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 2 }}>
        Cuestionario de incontinencia (ICIQ-SF)
      </Typography>

      {/* Score summary panel */}
      <Paper
        elevation={0}
        sx={{
          mb: 3, p: 2, borderRadius: 3,
          border: `2px solid ${interpretacion.color}`,
          background: interpretacion.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: interpretacion.color, fontWeight: 700, letterSpacing: 1.2 }}>
            Puntuación ICIQ-SF
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: interpretacion.color, lineHeight: 1 }}>
              {hasScore ? puntuacionTotal : '—'}
            </Typography>
            <Typography variant="body2" sx={{ color: interpretacion.color }}>/ 21</Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Chip
            label={interpretacion.label}
            sx={{ backgroundColor: interpretacion.color, color: '#fff', fontWeight: 700, fontSize: 14, px: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Chip size="small" label={`P1: ${pFrecuencia ?? '—'}`} variant="outlined" sx={{ borderColor: interpretacion.color, color: interpretacion.color }} />
            <Chip size="small" label={`P2: ${pCantidad ?? '—'}`}   variant="outlined" sx={{ borderColor: interpretacion.color, color: interpretacion.color }} />
            <Chip size="small" label={`P3: ${fields.impactoVida || '—'}`} variant="outlined" sx={{ borderColor: interpretacion.color, color: interpretacion.color }} />
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: VIEWER_COLORS.labelMuted, fontWeight: 600 }}>
              1. ¿Con qué frecuencia pierde orina?
            </Typography>
            {pFrecuencia != null && (
              <Chip label={`${pFrecuencia} pt${pFrecuencia !== 1 ? 's' : ''}`} size="small"
                sx={{ backgroundColor: '#dbeafe', color: '#1e3a8a', fontWeight: 700 }} />
            )}
          </Box>
          <ReadOnlyField label="Frecuencia" value={fields.frecuencia} />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: VIEWER_COLORS.labelMuted, fontWeight: 600 }}>
              2. Indique la cantidad de orina que cree que se le escapa.
            </Typography>
            {pCantidad != null && (
              <Chip label={`${pCantidad} pts`} size="small"
                sx={{ backgroundColor: '#dbeafe', color: '#1e3a8a', fontWeight: 700 }} />
            )}
          </Box>
          <ReadOnlyField label="Cantidad" value={fields.cantidad} />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: VIEWER_COLORS.labelMuted, fontWeight: 600 }}>
              3. ¿En qué medida estos escapes han afectado su vida diaria?
            </Typography>
            <Chip label={`${fields.impactoVida || '0'} pts`} size="small"
              sx={{ backgroundColor: '#dbeafe', color: '#1e3a8a', fontWeight: 700 }} />
          </Box>
          <ReadOnlyField label="Impacto en vida diaria (0-10)" value={fields.impactoVida} />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ color: VIEWER_COLORS.labelMuted, fontWeight: 600, mb: 0.5 }}>
            4. ¿Cuándo pierde orina? <Typography component="span" variant="caption" sx={{ color: '#94a3b8' }}>(diagnóstica, no puntúa)</Typography>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {fields.factores.length > 0
              ? fields.factores.map((f) => (
                  <Chip key={f} label={f} sx={{ borderColor: VIEWER_COLORS.surfaceBorder, fontWeight: 500 }} variant="outlined" />
                ))
              : <Typography sx={{ color: VIEWER_COLORS.textMuted }}>Sin factores registrados</Typography>
            }
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export const CuestionarioViewerActions = ({ onExportPDF, onEdit }) => (
  <Paper
    className="entrevista-viewer__actions"
    elevation={0}
    sx={{
      mt: 2,
      p: 2,
      borderRadius: 4,
      border: `1px solid ${VIEWER_COLORS.surfaceBorder}`,
      backgroundColor: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(10px)',
    }}
  >
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" useFlexGap flexWrap="wrap">
      <Button variant="outlined" color="primary" onClick={onEdit} sx={{ borderRadius: 2.5 }}>
        Editar
      </Button>
      <Button variant="contained" color="secondary" onClick={onExportPDF} sx={{ borderRadius: 2.5 }}>
        Exportar PDF
      </Button>
    </Stack>
  </Paper>
);
